import React, { useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { actionPlanCollaboratorsFields } from '../../../graphql/ActionPlans/ActionPlanFields';
import {
  ActionPlanCollaboratorsSchema,
  ActionPlanTaskSchema,
} from '../../../definitions/schemas';
import { capitalize } from '../../../lib/String';
import {
  DATETIME_FORMAT_SHORT,
  formatDateTime,
  getDateFromISO,
} from '../../../lib/Date';
import { deduplicateCollaborators, findCollaborator } from '../helper';
import { DRAWER_Z_INDEX } from '../../../definitions/constants';
import {
  Event as AnalyticsEvent,
  Events,
  Properties,
} from '../../../enums/Analytics';
import { getDisplayName } from '../../../lib/DisplayName';
import ActionPlansInterface from '../../../graphql/ActionPlans/ActionPlansInterface';
import ActionPlansTasksInterface, {
  UpdateActionPlanTaskInput,
} from '../../../graphql/ActionPlans/ActionPlanTasksInterface';
import Analytics from '../../../tools/Analytics';
import InlineDatePicker from '../../Date/InlineDatePicker';
import OwnerSelection from './Owners/OwnerSelection';
import useResource from '../../../lib/hook/useResource';

interface Props {
  actionPlanId?: string;
  isAuthenticatedUser: boolean;
  onDiscard: (latestTaskVersion: ActionPlanTaskSchema) => void;
  onOverwrite: (payload: UpdateActionPlanTaskInput) => Promise<void>;
  open: boolean;
  payload: UpdateActionPlanTaskInput;
  referrer: string;
  taskOwnersOptions: ActionPlanCollaboratorsSchema;
  userContactId?: string;
}

const ConflictResolutionDialog: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const {
    actionPlanId,
    isAuthenticatedUser,
    onDiscard,
    onOverwrite,
    open,
    payload,
    referrer,
    taskOwnersOptions,
    userContactId,
  } = props;

  const [actionPlanCollaborators, actionPlanCollaboratorsReady] = useResource(
    () => loadActionPlanCollaborators(),
    [],
    [actionPlanId],
    open && !!actionPlanId
  );

  const [latestVersion, latestVersionReady] = useResource(
    () => loadTask(),
    undefined,
    [payload.id],
    open
  );

  const dedupedOwnersOptions = useMemo(
    () => deduplicateCollaborators(taskOwnersOptions, actionPlanCollaborators),
    [actionPlanCollaborators, taskOwnersOptions]
  );

  const loadActionPlanCollaborators = async () => {
    const actionPlan = await new ActionPlansInterface(userContactId).get(
      { id: actionPlanId! },
      actionPlanCollaboratorsFields
    );

    return actionPlan.collaborators || [];
  };

  const loadTask = () =>
    new ActionPlansTasksInterface(userContactId).get({ id: payload.id });

  const handleDiscard = () => {
    if (latestVersion) {
      onDiscard(latestVersion);

      trackCollisionResolvedEvent(
        'keep latest version',
        isAuthenticatedUser
          ? Events.MAP_TASK_USER_COLLISION_RESOLVED
          : Events.MAP_TASK_CONTACT_COLLISION_RESOLVED
      );
    }
  };

  const handleOverwrite = () => {
    if (latestVersion) {
      onOverwrite({
        ...payload,
        revision: latestVersion.revision,
      });

      trackCollisionResolvedEvent(
        'overwrite with changes',
        isAuthenticatedUser
          ? Events.MAP_TASK_USER_COLLISION_RESOLVED
          : Events.MAP_TASK_CONTACT_COLLISION_RESOLVED
      );
    }
  };

  const trackCollisionResolvedEvent = (
    resolution: string,
    event: AnalyticsEvent
  ) => {
    Analytics.track(event, {
      [Properties.ENTERED_FROM]: referrer,
      [Properties.RESOLUTION_SELECTED]: resolution,
    });
  };

  const renderUpdateInfo = (
    task: ActionPlanTaskSchema,
    isCurrentUser?: boolean
  ): React.ReactNode => {
    const updatedAt = task.updatedAt
      ? formatDateTime(task.updatedAt, DATETIME_FORMAT_SHORT)
      : 'Just now';

    const responsible = findCollaborator(
      dedupedOwnersOptions,
      task.updatedById,
      task.updatedByType
    );

    const name = responsible
      ? getDisplayName(responsible, { fullEmail: true })
      : null;

    const updatedAtNode = (
      <Typography color="textSecondary" component="p" noWrap variant="caption">
        {isCurrentUser ? <>&nbsp;</> : updatedAt}
      </Typography>
    );

    const nameNode = (
      <Typography color="textSecondary" component="p" noWrap variant="caption">
        {isCurrentUser || !name ? <>&nbsp;</> : <>By {name}</>}
      </Typography>
    );

    return (
      <>
        {updatedAtNode}
        {nameNode}
      </>
    );
  };

  const renderTask = (
    label: string,
    actionLabel: string,
    actionHandler: () => void,
    task?: ActionPlanTaskSchema,
    isCurrentUser?: boolean
  ): JSX.Element => {
    if (!(task && actionPlanCollaboratorsReady)) {
      return (
        <Box display="flex" justifyContent="center">
          <CircularProgress color="secondary" size={32} />
        </Box>
      );
    }

    const { description, dueDate, name, owners } = task;
    const taskDueDate = dueDate ? getDateFromISO(dueDate) : null;

    return (
      <>
        <Box mb={0.5}>
          <Typography variant="body2">
            <Box component="strong">{label}</Box>
          </Typography>
        </Box>
        <Box mb={1}>{renderUpdateInfo(task, isCurrentUser)}</Box>
        <Typography noWrap title={name} variant="body2">
          {name}
        </Typography>
        <Typography
          color="textSecondary"
          component="p"
          noWrap
          title={description}
          variant="caption"
        >
          {description || '-'}
        </Typography>
        <Box alignItems="center" display="flex" my={2}>
          {owners && owners.length > 0 && (
            <Box mr={2}>
              <OwnerSelection
                alignLeft
                allowInvitations={false}
                collaborators={[]}
                disabled
                max={4}
                onChange={() => {}}
                options={dedupedOwnersOptions}
                owners={task.owners ?? []}
                searchParticipants={() => Promise.resolve([])}
                setCollaborators={() => {}}
              />
            </Box>
          )}
          {taskDueDate && (
            <InlineDatePicker
              disabled
              onChange={() => {}}
              value={taskDueDate}
            />
          )}
          {!owners?.length && !taskDueDate && <Box height={32}>&nbsp;</Box>}
        </Box>
        <Box my={2}>
          <Typography color="textSecondary" component="p" variant="caption">
            Status:{' '}
            <Typography color="textPrimary" component="span" variant="caption">
              {capitalize(task.status)}
            </Typography>
          </Typography>
          <Typography color="textSecondary" component="p" variant="caption">
            Milestone:{' '}
            <Typography color="textPrimary" component="span" variant="caption">
              {task.milestone ? 'True' : 'False'}
            </Typography>
          </Typography>
        </Box>
        <Box mx={-1}>
          <Button
            color="secondary"
            disabled={!latestVersionReady}
            onClick={actionHandler}
          >
            {actionLabel}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      open={open}
      style={{
        zIndex: DRAWER_Z_INDEX + 1,
      }}
    >
      <DialogTitle>This task was just updated</DialogTitle>
      <DialogContent>
        <Box pb={4}>
          <Box mb={3}>
            <Typography color="textSecondary" variant="body2">
              Would you like to keep the latest version or overwrite it with
              your changes?
            </Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} sm zeroMinWidth>
              {renderTask(
                'Latest Version',
                'Keep latest version',
                handleDiscard,
                latestVersion
              )}
            </Grid>
            <Divider flexItem orientation="vertical" />
            <Grid item xs={12} sm={6} zeroMinWidth>
              {renderTask(
                'Your changes',
                'Overwrite with your changes',
                handleOverwrite,
                payload as ActionPlanTaskSchema,
                true
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
